import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';

import { MODULE_NAMES } from '../../../Constants/modules';
import { CANCEL_TEXT } from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import {
  createInvoiceType,
  invoiceTypeListRequest,
  updateInvoiceType,
} from '../../../Api/request/invoiceType';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import '../RegionList/RegionList.scss';

const INITIAL_FORM = { invoiceTypeName: '' };

const TABLE_CONFIG = [
  {
    field: 'slNo',
    header: 'Sl. No',
    filter: false,
    visible: true,
    defaultPinned: true,
    sortable: false,
    order: 1,
    menuItems: [],
  },
  {
    field: 'invoiceTypeName',
    header: 'Invoice Type',
    filter: false,
    visible: true,
    sortable: false,
    order: 2,
    menuItems: [],
  },
  {
    field: 'status',
    header: 'Status',
    filter: false,
    visible: true,
    sortable: false,
    order: 3,
    menuItems: [],
  },
  {
    field: 'action',
    header: 'Action',
    className: 'region-list-action-column',
    headerClassName: 'region-list-action-column',
    width: 180,
    exportable: false,
    filter: false,
    visible: true,
    sortable: false,
    order: 4,
    menuItems: [],
  },
];

const isSuccessful = (response) => (
  Number.isInteger(response?.status) && response.status >= 200 && response.status < 300
);

const getResponseMessage = (response) => (
  response?.data?.response?.data?.message
  || response?.data?.response?.data?.externalMessage
  || response?.data?.message
  || ''
);

const renderDialog = ({ mode, form, hasError, onChange, onClose, onSubmit, isSubmitDisabled }) => createPortal(
  <div className="region-list-modal-backdrop">
    <form className="region-list-modal" onSubmit={onSubmit}>
      <div className="region-list-modal__header">
        {mode === 'edit' ? 'Edit Invoice Type' : 'Add Invoice Type'}
      </div>
      <div className="region-list-modal__body">
        <div className="region-list-modal__field">
          <label htmlFor="invoice-type-name">
            Invoice Type<span className="required-asterisk">*</span>
          </label>
          <input
            id="invoice-type-name"
            name="invoiceTypeName"
            type="text"
            maxLength={150}
            placeholder="Enter Invoice Type"
            value={form.invoiceTypeName}
            onChange={onChange}
            className={hasError ? 'is-error' : ''}
            required
          />
        </div>
      </div>
      <div className="region-list-modal__footer">
        <button type="button" className="region-list-modal__btn region-list-modal__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="region-list-modal__btn region-list-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const InvoiceType = () => {
  const { userProfile } = useAuth();
  const permission = userProfile?.permissions?.[MODULE_NAMES.MASTERS_INVOICE_TYPE];
  const isSuperAdmin = userProfile?.roleDetails?.some((role) => role.roleName === 'Super Admin');
  const normalizedPermissions = useMemo(
    () => (permission || []).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const aliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return aliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [dialog, setDialog] = useState({ mode: 'add', selected: null, isOpen: false });
  const [form, setForm] = useState(INITIAL_FORM);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toggleItem, setToggleItem] = useState(null);

  const loadInvoiceTypes = useCallback(async () => {
    if (!hasAccess('read')) {
      setInvoiceTypes([]);
      return;
    }
    try {
      const response = await UseAxios(invoiceTypeListRequest(), setIsLoading);
      if (!isSuccessful(response)) {
        setInvoiceTypes([]);
        return;
      }
      const rows = response?.data?.invoiceTypes || [];
      setInvoiceTypes(rows.map((row) => ({
        ...row,
        invoiceTypeId: row.invoiceTypeId ?? row.invoice_type_id,
        invoiceTypeName: row.invoiceTypeName ?? row.invoice_type_name,
        isActive: row.isActive ?? Number(row.is_active) === 1,
      })));
    } catch (_error) {
      setInvoiceTypes([]);
    }
  }, [hasAccess, setIsLoading]);

  useEffect(() => {
    loadInvoiceTypes();
  }, [loadInvoiceTypes]);

  const openAdd = useCallback(() => {
    setForm(INITIAL_FORM);
    setHasError(false);
    setDialog({ mode: 'add', selected: null, isOpen: true });
  }, []);

  const openEdit = useCallback((item) => {
    setForm({ invoiceTypeName: item.invoiceTypeName || '' });
    setHasError(false);
    setDialog({ mode: 'edit', selected: item, isOpen: true });
  }, []);

  const closeDialog = useCallback(() => {
    setForm(INITIAL_FORM);
    setHasError(false);
    setDialog({ mode: 'add', selected: null, isOpen: false });
  }, []);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (value.trim()) setHasError(false);
  }, []);

  const isSubmitDisabled = useMemo(() => (
    dialog.mode === 'edit'
    && form.invoiceTypeName.trim() === dialog.selected?.invoiceTypeName?.trim()
  ), [dialog.mode, dialog.selected, form.invoiceTypeName]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const invoiceTypeName = form.invoiceTypeName.trim();
    if (!invoiceTypeName) {
      setHasError(true);
      toast.error('Please enter invoice type.');
      return;
    }
    const request = dialog.mode === 'edit'
      ? updateInvoiceType(dialog.selected.invoiceTypeId, invoiceTypeName, dialog.selected.isActive ? 1 : 0)
      : createInvoiceType(invoiceTypeName);
    const response = await UseAxios(request, setIsLoading);
    if (!isSuccessful(response)) {
      toast.error(getResponseMessage(response) || 'Failed to save invoice type.');
      return;
    }
    toast.success(response?.data?.message || 'Invoice type saved successfully.');
    closeDialog();
    loadInvoiceTypes();
  }, [closeDialog, dialog.mode, dialog.selected, form.invoiceTypeName, loadInvoiceTypes, setIsLoading]);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleItem) return;
    const response = await UseAxios(
      updateInvoiceType(toggleItem.invoiceTypeId, toggleItem.invoiceTypeName, toggleItem.isActive ? 0 : 1),
      setIsLoading,
    );
    if (!isSuccessful(response)) {
      toast.error(getResponseMessage(response) || 'Failed to update invoice type status.');
      return;
    }
    toast.success(response?.data?.message || 'Invoice type status updated successfully.');
    setToggleItem(null);
    loadInvoiceTypes();
  }, [loadInvoiceTypes, setIsLoading, toggleItem]);

  const tableData = useMemo(() => invoiceTypes.map((item, index) => ({
    ...item,
    slNo: index + 1,
    status: item.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="region-list-actions-cell" onClick={(event) => event.stopPropagation()}>
        <div className="region-list-action-cell-inner">
          {hasAccess('update') && (
            <ControlledSwitches
              checked={Boolean(item.isActive)}
              switchCss="switchCss"
              handleChange={() => setToggleItem(item)}
            />
          )}
          {hasAccess('update') && (
            <button
              type="button"
              className="region-list-action-btn"
              title="Edit Invoice Type"
              aria-label={`Edit ${item.invoiceTypeName}`}
              onClick={() => openEdit(item)}
            >
              <img src={EditActionIcon} alt="" />
            </button>
          )}
        </div>
      </div>
    ),
  })), [hasAccess, invoiceTypes, openEdit]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || {},
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showAppliedFilters: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Invoice Type',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: TABLE_CONFIG.filter((column) => column.field !== 'action' || hasAccess('update')),
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: hasAccess('update') ? 4 : 3 },
      paginationConfig: {
        page: 1,
        limit: Math.max(tableData.length, 1000),
        total: tableData.length,
        rowsPerPageOptions: [5, 10, 25, 50],
      },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length, userProfile]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view invoice types.</div>;
  }

  return (
    <div className="region-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && renderDialog({
        mode: dialog.mode,
        form,
        hasError,
        onChange: handleChange,
        onClose: closeDialog,
        onSubmit: handleSubmit,
        isSubmitDisabled,
      })}

      <FormDialog
        open={Boolean(toggleItem)}
        title={toggleItem?.isActive ? 'Disable Invoice Type' : 'Enable Invoice Type'}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon"><img alt="Alert Icon" className="icon-image" src={alertIcon} /></div>
            <div className="dialog-message-text">
              {toggleItem?.isActive
                ? 'Do you want to disable this invoice type?'
                : 'Do you want to enable this invoice type?'}
            </div>
          </div>
        )}
        button1={CANCEL_TEXT}
        cancelButtonId="cancel-invoice-type-toggle"
        button2={toggleItem?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="invoice-type-toggle"
        button1Action={() => setToggleItem(null)}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default InvoiceType;
